import { useEffect, useMemo, useState } from "react";
import {XCircleIcon} from "@heroicons/react/24/outline";
import {CheckCircleIcon, CheckIcon, Square2StackIcon} from "@heroicons/react/24/solid";
import {ExclamationTriangleIcon} from "@heroicons/react/24/outline";
import {useAlert, useApi, useTenant} from "../../hooks";
import { useNavigate, useParams } from "react-router";
import { useLoaderStore, useSystemTenantStore, useTheme, useUserStore } from "../../context";
import dateHelpers from "../../utils/dateHelpers";
import { formatCurrency } from "../../utils/helpers";
import { isNil } from "lodash";
import { ConfirmationModal, ModalWrapper } from "../../components/Modals";
import { NumberInput, SelectInput, TextInput } from "../../components/Inputs";
import {Button, GoBack, PrimaryButton} from "../../components/Buttons";
import constants from "../../utils/constants";
import {useActiveTenant, useTenantTheme} from "../../context/useSystemTenantStore";
import {clsx} from "clsx";
import { ActiveFilter, PrimaryTable } from "../../components/Tables";

function SystemTenantCustomer() {
    const { id } = useParams();
    const { fetch, post } = useApi();
    const alert = useAlert();
    const { currentUser, impersonating, impersonate} = useUserStore();
    const { setShowLoader } = useLoaderStore();
    const navigate = useNavigate();
    const activeTenant = useActiveTenant();
    const theme = useTenantTheme();
    const [customer, setCustomer] = useState();
    const [selectedProduct, setSelectedProduct] = useState();
    const [payments, setPayments] = useState([])
    const [activeProducts, setActiveProducts] = useState([])
    const [pastProducts, setPastProducts] = useState([])
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [refundModalOpen, setRefundModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(()=>{
        getCustomer();
        getCustomerPurchaseHistory();
    }, []);

    function getCustomer() {
        fetch(`systemTenant/GetSystemTenantCustomer/${id}/${activeTenant?.id}`)
        .then(res => {
            setCustomer(res);
        })
        .catch(err => {
            alert("Server error", "Error fetching customer information", "error");
        });
    }

    function getCustomerPurchaseHistory(){
        fetch(`systemTenant/GetCustomerPurchaseHistoryBySystemTenant/${id}/${activeTenant?.id}`)
        .then(res => {
            if (res.payments && res.payments.length) {
                res.payments.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setPayments(res.payments);
            }
            if(res.products && res.products.length) {
                let oldProducts = res.products.filter(p => (
                    !res.activeProducts.some(ap => ap.productId === p.productId)));
                oldProducts.sort((a, b) => new Date(b.effectiveAt) - new Date(a.effectiveAt));
                setPastProducts(oldProducts);
            }
            if (res.activeProducts && res.activeProducts.length) {
                setActiveProducts(res.activeProducts);
                let subscriptions = res.activeProducts.filter(x => x.productType === "Primary License");
                let paidProducts = subscriptions.filter(x => x.price > 0);
                if (paidProducts.length) {
                    paidProducts.sort((a, b) => a.effectiveAt - b.effectiveAt);
                    // setActiveSubscription(paidProducts[0]);
                    return;
                }
                subscriptions.sort((a, b) => a.effectiveAt - b.effectiveAt);
                // setActiveSubscription(subscriptions[0]);
            }
        })
        .catch(err => {
            alert("Server error", "Unable to retrieve purchase history", "error");
        })
    }

    function sendForgotPasswordEmail() {
        post("public/forgotPassword", customer.email)
        .then((res) => {
            alert("Success!", "Check your inbox for a reset password email", "success", false);
        })
        .catch((err) => {
            alert("Server error", "Unable to send a reset password email", "error");
        })
        .finally(() => {
        // setShowLoader(false);
        });
    }

    function processRefund(refundAmount, refundReason) {
        if(isLoading) return;
        if (!selectedProduct) {
            alert("Error", "Could not identify which product to refund", "warning");
            return;
        }
        if (!refundAmount) {
            alert("Error", "Invalid refund amount", "warning");
            return;
        }
        if (refundAmount > selectedProduct.price) {
            alert("Error", "Invalid refund amount, amount is greater than the product's price", "warning");
            return; 
        }
        let payload = {
            PartialRefundAmount: refundAmount,
            RefundReason: refundReason,
            PartialRefund: refundAmount < selectedProduct.price,
            OwnedProductId: selectedProduct.ownedProductId,
            NotifyTenantUsers: true
        };
        setIsLoading(true);
        post(`product/processRefund/${activeTenant?.id}`, payload)
        .then(res => {
            alert("Product refunded", "Payment has been refunded to customer", "success");
            setSelectedProduct(undefined);
            setRefundModalOpen(false);
            window.location.reload();
        })
        .catch(err => {
            alert("Server error", "Failed to refund product payment to customer", "error");
        })
        .finally(() => {
            setIsLoading(false);
        });
    }

    function cancelSubscription() {
        if(isLoading) return;
        setIsLoading(true);
        fetch(`product/cancelSubscription/${selectedProduct.ownedProductId}/${activeTenant?.id}/`)
        .then(res => {
            alert("Product removed", `Product has been canceled and will expire at ${dateHelpers.utcToLocalShortDate(selectedProduct.effectiveThrough)}`, "success");
            setSelectedProduct(undefined);
            setCancelModalOpen(false);
            window.location.reload();
        })
        .catch(err => {
            alert("Server error", "Failed to remove product from customer", "error");
        })
        .finally(() => {
            setIsLoading(false);
        });
    }

    const handleImpersonation = (user) => {
        if (impersonating) {
          alert(
            "Already impersonating",
            "Stop impersonating to impersonate a different user",
            "warning"
          );
          return;
        }
        setShowLoader(true);
    
        post(`user/impersonate/${id}`)
          .then((res) => {
            impersonate(res.user, res.token);
            alert("Success", `Impersonating ${res.username}`, "success");
            // NAVIGATE HERE PLUS SLUG IN THE BEGINNING - window.location.host
            let baseUrl = window.location.host;
            let subdomain = activeTenant?.slug;
            let productHomePage = `${window.location.protocol}//${subdomain}.${baseUrl}`;
            window.location.href = productHomePage;
          })
          .catch((err) => {
            alert(
              "Error",
              err.data.message || "Could not impersonate user",
              "error"
            );
            console.error(err);
          })
          .finally(() => {
            setShowLoader(false);
          });
      };

    if(!customer) return null;

    const cardStyle = "bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden";
    const scrollArea = "max-h-96 overflow-y-auto overflow-x-auto custom-scrollbar";
    const tableHeaderStyle = "px-4 py-3 bg-gray-100 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200";
    const tableFirstHeaderStyle = "px-4 py-3 bg-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200";

    return (
        <div className={clsx("p-8 flex flex-col gap-6 bg-gray-50 min-h-screen")}>
        {/*<div className="p-4">*/}
            <div className="mt-4  gap-y-4">
                <div className="ml-4 flex flex-row justify-between">
                    <GoBack display="Return to Customers" navigateTo="/customers" />
                    <div className="space-x-4 flex flex-row">
                        <Button
                            text="Send Forgot Password Email"
                            onClick={sendForgotPasswordEmail}
                            disabled={isLoading}
                            isLoading={isLoading}
                            icon="fa-solid fa-envelope"
                        />
                        <Button
                            text="Impersonate"
                            onClick={handleImpersonation}
                            disabled={isLoading}
                            backgroundColor={theme?.secondaryHexColor}
                            overrideThemeColors
                            />
                        {/*<button*/}
                        {/*    className="rounded-full inline-block inline-block px-4 py-2  font-semibold text-white bg-primaryColor"*/}
                        {/*    style={{backgroundColor: theme?.primaryHexColor}}*/}
                        {/*    onClick={sendForgotPasswordEmail}*/}
                        {/*>*/}
                        {/*    Send Forgot Password Email*/}
                        {/*</button>*/}
                        {/*<button*/}
                        {/*    className="rounded-full inline-block inline-block px-4 py-2  font-semibold text-white bg-violet-700"*/}
                        {/*    onClick={handleImpersonation}*/}
                        {/*>*/}
                        {/*    Impersonate*/}
                        {/*</button>*/}
                    </div>

                </div>
                {/* Customer Information Card */}
                <div className={clsx(cardStyle, "p-5 max-w-md")}>
                    <h2 className={clsx("text-sm font-bold text-gray-400 uppercase tracking-wider mb-4")}>
                        Customer Information
                    </h2>
                    <div className={clsx("space-y-4")}>
                        {/* Stacked Name */}
                        <div className={clsx("flex flex-col")}>
                            <span className={clsx("text-xs font-medium text-gray-500 uppercase")}>Full Name</span>
                            <span className={clsx("text-base font-semibold text-gray-900")}>{customer.firstName} {customer.lastName}</span>
                        </div>

                        {/* Stacked Email */}
                        <div className={clsx("flex flex-col")}>
                            <span className={clsx("text-xs font-medium text-gray-500 uppercase")}>Email Address</span>
                            <span className={clsx("text-base font-semibold text-gray-900")}>{customer.email}</span>
                        </div>
                    </div>
                </div>
                {/*<PageHeader title={`${customer.firstName} ${customer.lastName}`} />*/}
                {/*<div className="bg-white rounded-lg flex flex-col p-4">*/}
                {/*    <DetailLine label="Email:" value={customer.email}/>*/}
                {/*</div>*/}
            </div>

            {/* Active Products and Subscriptions */}
            <div className={clsx(cardStyle, "mt-4")}>
                <div className={clsx("px-6 py-4 border-b border-gray-200")}>
                    <h2 className={clsx("text-lg font-bold text-gray-500")}>Active Products and Subscriptions</h2>
                </div>
                <div className={clsx(scrollArea)}>
                    <table className={clsx("min-w-full divide-y divide-gray-200")}>
                        <thead className={clsx("sticky top-0 z-10")}>
                        <tr>
                            <th className={clsx(tableHeaderStyle)}>Name</th>
                            <th className={clsx(tableHeaderStyle)}>Type</th>
                            <th className={clsx(tableHeaderStyle)}>Effective At</th>
                            <th className={clsx(tableHeaderStyle)}>Expires At</th>
                            <th className={clsx(tableHeaderStyle)}>Qty</th>
                            <th className={clsx(tableHeaderStyle)}>Unit Qty</th>
                            <th className={clsx(tableHeaderStyle)}>Price</th>
                            <th className={clsx(tableHeaderStyle)}>Will Renew</th>
                            <th className={clsx(tableHeaderStyle)}>Status</th>
                            <th className={clsx(tableHeaderStyle, "text-right")}></th>
                        </tr>
                        </thead>
                        <tbody className={clsx("bg-white divide-y divide-gray-200")}>
                        {activeProducts.map((product, idx) => (
                                <ProductLine
                                    key={`activeproduct-${idx}`}
                                    index={idx}
                                    productName={product.name}
                                    productType={product.productType}
                                    effectiveAt={product.effectiveAt}
                                    effectiveThrough={product.effectiveThrough}
                                    qty={product.qty}
                                    unitQty={product.supplementalFeatureQty || "N/A"}
                                    price={product.price}
                                    willRenew={!isNil(product.renewalProductId)}
                                    status={product.status}
                                    setCancelOpen={setCancelModalOpen}
                                    setRefundOpen={setRefundModalOpen}
                                    setSelectedProduct={setSelectedProduct}
                                    product={product}
                                    isActive
                                />
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {/*<div className="mt-4">*/}
            {/*    <PageHeader title={`Active Products and Subsriptions`} />*/}
            {/*    <div className="bg-white rounded-lg flex flex-col p-4 divide-y-2 divide-slate-100">*/}
            {/*        <GridHeaders headerList={["Name", "Type", "Effective At", "Expires At", "Qty", "Unit Qty", "Price", "Will Renew", "Status", ""]}/>*/}
            {/*        {activeProducts.map((product, idx) => (*/}
            {/*            <ProductLine*/}
            {/*                key={`activeproduct-${idx}`}*/}
            {/*                productName={product.name}*/}
            {/*                productType={product.productType}*/}
            {/*                effectiveAt={product.effectiveAt}*/}
            {/*                effectiveThrough={product.effectiveThrough}*/}
            {/*                qty={product.qty}*/}
            {/*                unitQty={product.supplementalFeatureQty || "N/A"}*/}
            {/*                price={product.price}*/}
            {/*                willRenew={!isNil(product.renewalProductId)}*/}
            {/*                status={product.status}*/}
            {/*                setCancelOpen={setCancelModalOpen}*/}
            {/*                setRefundOpen={setRefundModalOpen}*/}
            {/*                setSelectedProduct={setSelectedProduct}*/}
            {/*                product={product}*/}
            {/*                isActive*/}
            {/*            />*/}
            {/*        ))}*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/* 3. Past Products and Subscriptions */}
            <div className={clsx(cardStyle, "mt-4")}>
                <div className={clsx("px-6 py-4 border-b border-gray-200")}>
                    <h2 className={clsx("text-lg font-bold text-gray-500")}>Past Products and Subscriptions</h2>
                </div>
                <div className={clsx(scrollArea)}>
                    <table className={clsx("min-w-full divide-y divide-gray-200")}>
                        <thead className={clsx("sticky top-0 z-10")}>
                        <tr>
                            <th className={clsx(tableHeaderStyle)}>Name</th>
                            <th className={clsx(tableHeaderStyle)}>Type</th>
                            <th className={clsx(tableHeaderStyle)}>Effective At</th>
                            <th className={clsx(tableHeaderStyle)}>Expires At</th>
                            <th className={clsx(tableHeaderStyle)}>Qty</th>
                            <th className={clsx(tableHeaderStyle)}>Unit Qty</th>
                            <th className={clsx(tableHeaderStyle)}>Price</th>
                            <th className={clsx(tableHeaderStyle)}>Will Renew</th>
                            <th className={clsx(tableHeaderStyle)}>Status</th>
                        </tr>
                        </thead>
                        <tbody className={clsx("bg-white divide-y divide-gray-200")}>
                        {pastProducts.map((product, idx) => (
                            <ProductLine
                                key={`pastproduct-${idx}`}
                                productName={product.name}
                                productType={product.productType}
                                effectiveAt={product.effectiveAt}
                                effectiveThrough={product.effectiveThrough}
                                qty={product.qty}
                                unitQty={product.supplementalFeatureQty || "N/A"}
                                price={product.price}
                                willRenew={!isNil(product.renewalProductId)}
                                status={product.status}
                            />
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {/*<div className="mt-4">*/}
            {/*    <PageHeader title={`Past Products and Subsriptions`} />*/}
            {/*    <div className="bg-white rounded-lg flex flex-col p-4 divide-y-2 divide-slate-100">*/}
            {/*        <GridHeaders headerList={["Name", "Type", "Effective At", "Expires At", "Qty", "Unit Qty", "Price", "Will Renew", "Status"]}/>*/}
            {/*        {pastProducts.map((product, idx) => (*/}
            {/*            <ProductLine*/}
            {/*                key={`pastproduct-${idx}`}*/}
            {/*                productName={product.name}*/}
            {/*                productType={product.productType}*/}
            {/*                effectiveAt={product.effectiveAt}*/}
            {/*                effectiveThrough={product.effectiveThrough}*/}
            {/*                qty={product.qty}*/}
            {/*                unitQty={product.supplementalFeatureQty || "N/A"}*/}
            {/*                price={product.price}*/}
            {/*                willRenew={!isNil(product.renewalProductId)}*/}
            {/*                status={product.status}*/}
            {/*            />*/}
            {/*        ))}*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/* Payment History */}
            <div className={clsx(cardStyle, "mt-4")}>
                <div className={clsx("px-6 py-4 border-b border-gray-200")}>
                    <h2 className={clsx("text-lg font-bold text-gray-500")}>Payment History</h2>
                </div>
                <div className={clsx(scrollArea)}>
                    <table className={clsx("min-w-full divide-y divide-gray-200")}>
                        <thead className={clsx("sticky top-0 z-10")}>
                        <tr>
                            <th className={clsx(tableFirstHeaderStyle, "text-start")}>Succeeded</th>
                            <th className={clsx(tableHeaderStyle)}>Amount</th>
                            <th className={clsx(tableHeaderStyle)}>Product</th>
                            <th className={clsx(tableHeaderStyle)}>Date</th>
                            <th className={clsx(tableHeaderStyle)}>Failed Reason</th>
                        </tr>
                        </thead>
                        <tbody className={clsx("bg-white divide-y divide-gray-200")}>
                        {payments && payments.length && payments.map((payment, idx) => (
                            <PaymentLine
                                key={`payment-${idx}`}
                                amount={payment.amount}
                                productName={payment.ownedProductName}
                                completedAt={payment.succeededAt || payment.failedAt}
                                succeeded={!payment.failedAt}
                                failMessage={payment.failureMessage}
                            />))}
                        </tbody>
                    </table>
                </div>
            </div>


            {/*<div className="mt-4">*/}
            {/*    <PageHeader title={`Payment History`} />*/}
            {/*    <div className="bg-white rounded-lg flex flex-col justify-center p-4 divide-y-2 divide-slate-100">*/}
            {/*        <GridHeaders headerList={["Succeded", "Amount", "Product", "Date", "Failed Reason"]}/>*/}
            {/*        {payments && payments.length && payments.map((payment, idx) => (*/}
            {/*            <PaymentLine */}
            {/*                key={`payment-${idx}`}*/}
            {/*                amount={payment.amount} */}
            {/*                productName={payment.ownedProductName}*/}
            {/*                completedAt={payment.succeededAt || payment.failedAt}*/}
            {/*                succeeded={!payment.failedAt}*/}
            {/*                failMessage={payment.failureMessage}*/}
            {/*            />))}*/}
            {/*    </div>*/}
            {/*</div>*/}
            {/* <div className="grid grid-cols-8"></div> */}
            <ConfirmationModal
                title={"Cancel Subscription"}
                message={`Are you sure you want to cancel this subscription?`}
                textToMatch={currentUser?.username}
                modalOpen={cancelModalOpen}
                setModalOpen={setCancelModalOpen}
                confirmButtonText={"Yes"}
                onConfirm={cancelSubscription}
            />
            <RefundConfirmationModal
                title={"Refund"}
                message={`Are you sure you want to cancel and refund this subscription?`}
                productPrice={selectedProduct?.price}
                textToMatch={currentUser?.username}
                modalOpen={refundModalOpen}
                setModalOpen={setRefundModalOpen}
                confirmButtonText={"Confirm"}
                onConfirm={processRefund}
            />
            {/* <div className="grid grid-cols-8 grid-cols-9"></div> */}
        </div>
    )
}


function PaymentLine({amount, productName, completedAt, succeeded, failMessage}) {
    const rowClass = 'text-sm font-medium text-tempest text-center';
    return (<tr className="py-4">
        <td className="flex justify-start ml-8">{succeeded
            ? (<CheckCircleIcon className="w-9 text-success" />)
            : (<XCircleIcon className="w-9 text-error" />)}</td>
        <td className={clsx(rowClass)}>{formatCurrency(amount, true)}</td>
        <td className={clsx(rowClass)}>{productName}</td>
        <td className={clsx(rowClass)}>{dateHelpers.utcToLocalShortDate(completedAt)}</td>
        <td className={clsx(rowClass)}>{failMessage}</td>
    </tr>);
};

function ProductLine({index, productName, price, productType, effectiveAt, effectiveThrough, qty, unitQty, willRenew, status, isActive, setCancelOpen, setRefundOpen, setSelectedProduct, product}) {
    const rowClass = 'text-sm font-medium text-tempest text-center';
    return (
        <tr
            key={`product-${productName}-${index}`}
            className={`gap-4 py-4 border-b-2 border-slate-100`}>
        <td className={clsx(rowClass)}>{productName}</td>
        <td className={clsx(rowClass)}>{productType}</td>
        <td className={clsx(rowClass)}>{dateHelpers.utcToLocalShortDate(effectiveAt)}</td>
        <td className={clsx(rowClass)}>{dateHelpers.utcToLocalShortDate(effectiveThrough)}</td>
        <td className={clsx(rowClass)}>{qty}</td>
        <td className={clsx(rowClass)}>{unitQty}</td>
        <td className={clsx(rowClass)}>{price ? formatCurrency(price, true) : "free"}</td>
        <td className={clsx(rowClass)}>{willRenew ? "Yes" : "No"}</td>
        <td className={clsx(rowClass)}>{status}</td>
        {isActive && (
            <td className="lg:col-span-1 col-span-10 text-lg flex lg:flex-row lg:justify-center gap-x-2">
                {product.productType.toLowerCase() === "primary license" && (<button className={clsx("border-2 px-2 py-2",
                    "rounded-md text-white bg-error shadow-md marker:inline-flex items-center justify-center h-[38px]",
                    " justify-center text-sm font-bold")} onClick={()=>{
                    setSelectedProduct(product);
                    setCancelOpen(true);
                }}>Remove</button>)}
                <button className={clsx("border-2 px-2 py-2",
                    "rounded-md text-white bg-amber-600 shadow-md marker:inline-flex items-center justify-center h-[38px]",
                    " justify-center text-sm font-bold")} onClick={()=>{
                    setSelectedProduct(product)
                    setRefundOpen(true);
                }}>Refund</button>
            </td>)}
    </tr>);
};

function RefundConfirmationModal({
    modalOpen,
    title,
    message,
    textToMatch,
    confirmButtonText,
    setModalOpen,
    onConfirm,
    productPrice
  }) {
    const [userInput, setUserInput] = useState("");
    const [refundAmount, setRefundAmount] = useState();
    const [refundReason, setRefundReason] = useState();
  
    const userInputMatches = useMemo(
      () => textToMatch === userInput,
      [textToMatch, userInput]
    );
  
    return (
      <ModalWrapper open={modalOpen} setOpen={setModalOpen}>
        <div className="w-[85vw] lg:w-[35vw]">
          <div className="pb-4 text-center flex items-center flex-col">
            {title}
          </div>
          {message && (
            <div className="pb-8 mx-6 text-center text-sm text-gray-500">
              {message}
            </div>
          )}
  
          <div className="mb-8">
            <TextInput
              label={"Enter username to confirm"}
              placeholder={textToMatch}
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              iconColor={userInputMatches ? "success" : "notification"}
              Icon={userInputMatches ? CheckIcon : ExclamationTriangleIcon}
            />
          </div>
          <div className="mb-8">
            <SelectInput
                label={"Refund reason (optional)"}
                placeholder={constants.REFUND_REASONS[0].label}
                options={constants.REFUND_REASONS}
                value={refundReason}
                onChange={(reason) => {setRefundReason(reason)} }
                onClear={() => {setRefundReason(undefined)}}
            />
          </div>
          <div className="mb-8">
            <NumberInput
              placeholder={productPrice}
              label={"Enter refund amount as pennies"}
              value={refundAmount}
              onChange={(e) => setRefundAmount(isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
              iconColor={userInputMatches ? "success" : "notification"}
              Icon={userInputMatches ? CheckIcon : ExclamationTriangleIcon}
            />
          </div>
          {refundAmount && refundAmount !== 0 
          ? (<div className="mb-8">
                <span>Amount: </span>
                <span>{formatCurrency(refundAmount, true)}</span>
            </div>) 
          : null}
  
          <div className="mt-3">
            <PrimaryButton
              text={confirmButtonText}
              onClick={() => {onConfirm(refundAmount, refundReason?.value)}}
              disabled={!userInputMatches}
            />
          </div>
        </div>
      </ModalWrapper>
    );
}

export default SystemTenantCustomer;